// How numbers and time labels look in every chart.
//
// InfluxDB hands back full float precision, and ECharts prints whatever it is
// given: a tooltip reading "Distribution of electricity: 665.123123421431242134"
// is not a reading anybody takes, it is a number that pushed the label off the
// card. Nothing upstream needs to change — the raw value stays in the series and
// in the payload, this only decides what is drawn.
//
// Applied through applyChartFormatting(), which the <EChart> wrapper in
// components/charts/EChart.js calls for every chart in the application. It only
// FILLS IN what a chart did not specify: a formatter written by hand for a
// particular chart always wins.

/** ECharts hands time-series items over as [timestamp, value]. */
const scalarOf = v => (Array.isArray(v) ? v[v.length - 1] : v);

/**
 * Short, honest, and the same everywhere.
 *
 *   665.123123421431  → "665.12"      two decimals is past any sensor's accuracy
 *   665.0             → "665"         a trailing ".00" is noise
 *   0.0012            → "0.001"       below 1, two decimals would print "0.00"
 *                                      and claim the reading is zero
 */
export const fmtValue = value => {
  const raw = scalarOf(value);
  if (raw === null || raw === undefined || raw === '') return '—';

  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  if (Number.isInteger(n)) return String(n);

  const decimals = Math.abs(n) >= 1 ? 2 : 3;
  return String(Number(n.toFixed(decimals)));
};

/** '{value} A' → a function that formats the number and keeps the unit. */
const fromTemplate = template => v => template.replace('{value}', fmtValue(v));

const withAxisLabel = (axis, patch) => ({
  ...axis,
  axisLabel: { ...(axis.axisLabel || {}), ...patch },
});

/** A numeric axis gets rounded labels; a category or time axis is left alone. */
const formatValueAxis = axis => {
  if (!axis || typeof axis !== 'object') return axis;
  if (axis.type === 'category' || axis.type === 'time') return axis;

  const existing = axis.axisLabel?.formatter;
  if (typeof existing === 'function') return axis;
  if (typeof existing === 'string') {
    // only a template referring to the value is ours to rewrite
    return existing.includes('{value}')
      ? withAxisLabel(axis, { formatter: fromTemplate(existing) })
      : axis;
  }
  return withAxisLabel(axis, { formatter: fmtValue });
};

/**
 * A time axis crowds itself: at one point per minute over twelve hours ECharts
 * lays out more labels than the axis is wide and draws them on top of each
 * other. `hideOverlap` drops the ones that would collide instead, which is the
 * difference between a readable axis and a smear.
 */
const formatTimeAxis = axis => {
  if (!axis || typeof axis !== 'object') return axis;
  if (axis.type !== 'time' && axis.type !== 'category') return formatValueAxis(axis);
  if (axis.axisLabel?.hideOverlap !== undefined) return axis;
  return withAxisLabel(axis, { hideOverlap: true });
};

const mapAxis = (axis, fn) => {
  if (!axis) return axis;
  return Array.isArray(axis) ? axis.map(fn) : fn(axis);
};

const formatTooltip = tooltip => {
  if (!tooltip || typeof tooltip !== 'object') return tooltip;
  // a chart that writes its own formatter has already decided
  if (tooltip.formatter || tooltip.valueFormatter) return tooltip;
  return { ...tooltip, valueFormatter: fmtValue };
};

/**
 * Returns a copy — the caller's option object is never mutated.
 *
 * A key the chart did not set stays unset. Writing `xAxis: undefined` onto a
 * gauge or a pie is not the same as leaving it out: ECharts merges option
 * objects, and a key that is present carries a meaning of its own.
 */
export const applyChartFormatting = option => {
  if (!option || typeof option !== 'object') return option;

  const next = { ...option };
  if ('tooltip' in option) next.tooltip = mapAxis(option.tooltip, formatTooltip);
  if ('xAxis' in option) next.xAxis = mapAxis(option.xAxis, formatTimeAxis);
  if ('yAxis' in option) next.yAxis = mapAxis(option.yAxis, formatValueAxis);
  return next;
};
